
import React, { useEffect } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, RefreshCw, AlertCircle } from 'lucide-react';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import CategoryTabs from '@/components/financial-reporting/CategoryTabs';
import FinancialDataList from '@/components/financial-reporting/FinancialDataList';
import { useFinancialReporting } from '@/hooks/useFinancialReporting';

const FinancialReporting = () => {
  const { companyName } = useParams<{ companyName: string }>();
  const [searchParams] = useSearchParams();
  const categoryParam = searchParams.get('category');
  const decodedCompanyName = decodeURIComponent(companyName || '');
  
  const {
    isLoading,
    selectedCategory,
    financialData,
    error,
    handleCategoryChange,
    handleRefresh,
    setSelectedCategory
  } = useFinancialReporting(decodedCompanyName);
  
  // Set the category from URL parameter if it exists
  useEffect(() => {
    if (categoryParam && ['all', 'earnings', 'funding', 'expenses', 'growth'].includes(categoryParam)) {
      setSelectedCategory(categoryParam);
    }
  }, [categoryParam, setSelectedCategory]);

  const isFundingView = selectedCategory === 'funding';
  
  const pageTitle = isFundingView 
    ? 'Investment Tracking' 
    : 'Financial Reporting';

  const pageDescription = isFundingView
    ? `Funding rounds and investment activity for ${decodedCompanyName}`
    : `Financial health analysis for ${decodedCompanyName}`;

  const backLinkText = `Back to ${decodedCompanyName} Profile`;

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-1 pt-24 px-6 pb-16">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6 flex justify-between items-center">
            <Button 
              variant="ghost" 
              asChild
              className="text-redflags-gray hover:text-redflags-dark"
            >
              <Link to={`/companies/${companyName}`} className="flex items-center gap-2">
                <ArrowLeft className="w-4 h-4" />
                {backLinkText}
              </Link>
            </Button>
            
            <Button 
              variant="outline" 
              onClick={handleRefresh}
              disabled={isLoading}
              className="flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh Data
            </Button>
          </div>
          
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-redflags-dark mb-2">{pageTitle}</h1>
            <p className="text-redflags-gray mb-4">
              {pageDescription}
            </p>
            
            <Card className="border border-redflags-light/70 mb-6">
              <CardContent className="pt-6">
                {isFundingView ? (
                  <p className="text-sm text-gray-600 mb-2">
                    Our AI-powered investment tracker monitors funding rounds, acquisitions, and investment activities 
                    for {decodedCompanyName} to identify financial stability indicators.
                  </p>
                ) : (
                  <p className="text-sm text-gray-600 mb-2">
                    Our AI-powered financial analysis scans quarterly reports, funding announcements, and financial news
                    about {decodedCompanyName} to identify potential financial risks or opportunities.
                  </p>
                )}
                <p className="text-sm text-gray-600">
                  This data provides insight into the company's financial stability and growth trajectory, which may impact job security.
                </p>
              </CardContent>
            </Card>
            
            {error && (
              <Alert variant="destructive" className="mb-6">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            
            <CategoryTabs 
              selectedCategory={selectedCategory}
              onCategoryChange={handleCategoryChange}
            >
              <FinancialDataList 
                financialData={financialData}
                isLoading={isLoading}
              />
            </CategoryTabs>
          </div>
        </div>
      </main>
    </div>
  );
};

export default FinancialReporting;
